import { useFilters } from "@/components/filters";
import { Dialog, ModalHeader, ModalValueExtractor, useOpenModal } from "@/components/modal";
import { DataTable, useColumns } from "@/components/data-table";
import { CajaApertura } from "@/shared/types/caja-apertura";
import { formatDate, formatString } from "@/shared/formats";
import { DataFetcher } from "@/components/display";
import { Stack, StackProps } from "@mui/material";
import { Form, useForm } from "@/components/form";
import { useGetAllCajaActualAperturasQuery } from "../api/get-all-caja-actual-aperturas";
import { GetCajaActualAperturaQuery } from "../api/get-caja-actual-apertura";
import ViewCajaActualApertura from "./view-caja-actual-apertura";

export default function ViewCajaActualAperturaHistorial({
    ...props
}: StackProps) {
    const openModal = useOpenModal();

    const filterData = useFilters({
        filters: [
            { type: "dateRange", label: "Fecha de Apertura", fromName: "aperturaDesde", toName: "aperturaHasta", fromLabel: "Apertura Desde", toLabel: "Apertura Hasta" },
            { type: "dateRange", label: "Fecha de Cierre", fromName: "cierreDesde", toName: "cierreHasta", fromLabel: "Cierre Desde", toLabel: "Cierre Hasta" },
            { type: "pagination" }
        ]
    });

    const fetchData = useGetAllCajaActualAperturasQuery(filterData.filterData);

    const columnData = useColumns<CajaApertura>({
        key: "caja_aperturas",
        columns: [
            { key: "fechaApertura", header: "Apertura", render: row => formatDate(row.fechaApertura, true), width: "auto" },
            { key: "fechaCierre", header: "Cierre", render: row => formatDate(row.fechaCierre, true), width: "auto" },
            { key: "caja", header: "Caja", render: row => formatString(row.cajaNombre) },
            { key: "estado", header: "Estado", render: row => row.abierto ? "Abierto" : "Cerrado", width: "auto", align: "center" },
            { key: "creadoPor", header: "Creado Por", render: row => formatString(row.creadoPor), show: false },
            { key: "fechaCreacion", header: "Fecha de Creación", render: row => formatDate(row.fechaCreacion), align: "center", show: false },
            { key: "modificadoPor", header: "Modificado Por", render: row => formatString(row.modificadoPor), show: false },
            { key: "fechaModificacion", header: "Última Modificación", render: row => formatDate(row.fechaModificacion), align: "center", show: false },
        ]
    });

    const form = useForm({
        onSubmit: ({ value }) => {
            filterData.setFilterData({ ...filterData.filterData, ...value, page: 0 })
        },
        defaultValues: {
            aperturaDesde: "",
            aperturaHasta: "",
            cierreDesde: "",
            cierreHasta: ""
        },
        submitOnce: false,
        closeModalOnSubmit: false,
    })

    return <Stack gap={3} {...props}>
        <Stack gap={2} direction={"row"} justifyContent={"space-between"}>
            <Form form={form} gap={2} direction={"row"} flexWrap={"wrap"}>
                <Stack maxWidth={"200px"}>
                    <Form.DatePicker name="aperturaDesde" label="Apertura Desde" clearable size="small" />
                </Stack>
                <Stack maxWidth={"200px"}>
                    <Form.DatePicker name="aperturaHasta" label="Apertura Hasta" displayDateOffset={-1} clearable size="small" />
                </Stack>
                <Stack maxWidth={"200px"}>
                    <Form.DatePicker name="cierreDesde" label="Cierre Desde" clearable size="small" />
                </Stack>
                <Stack maxWidth={"200px"}>
                    <Form.DatePicker name="cierreHasta" label="Cierre Hasta" displayDateOffset={-1} clearable size="small" />
                </Stack>
                <Form.SubmitButton size="small">Filtrar</Form.SubmitButton>
            </Form>
        </Stack>
        <DataTable flex={1}>
            <DataTable.Content
                rows={fetchData.data?.result}
                columns={[
                    ...columnData.columns,
                    {
                        header: "Acción", width: "auto", align: "center", render: row => <DataTable.EntryOption>
                            <DataTable.EntryOption.ViewButton onClick={() => openModal("viewApertura", row.id)} />
                        </DataTable.EntryOption>
                    }
                ]}
                fit="flex"
                loading={fetchData.isFetching}
                error={fetchData.error}
            />
            <DataTable.Pagination filterData={filterData} queryResult={fetchData} />
        </DataTable>

        <Dialog code="viewApertura" size="md">
            <ModalHeader>Ver Arqueo de Caja</ModalHeader>
            <ModalValueExtractor>
                {cajaAperturaId => <GetCajaActualAperturaQuery id={cajaAperturaId}>
                    {query => <DataFetcher query={query}>
                        {cajaApertura => <ViewCajaActualApertura
                            cajaApertura={cajaApertura}
                            padding={3}
                        />}
                    </DataFetcher>}
                </GetCajaActualAperturaQuery>}
            </ModalValueExtractor>
        </Dialog>
    </Stack>
}